import { cloneDeep, get, isEmpty } from 'lodash';
import MdmsApi from 'client/api/v2/MdmsApi';

const getMdmDevices = (state = {}) => get(state, 'item.mdmDevices', []);

/**
 * This mixin provides common methods to determine when an organization has configured Apple MDM
 * for the org. It is used by the SettingsModel and SystemModel which are used by components that
 * need this information.
 */
export default {
  actions: {
    async createMdmSettings({ commit, state = {} }) {
      const mdmDevices = getMdmDevices(state);

      if (mdmDevices.length === 0) {
        const { appleMdm } = await MdmsApi.postMdm();
        commit('SET_ITEM', { mdmDevices: [cloneDeep(appleMdm)] });
        commit('SET_PREVIOUS_ITEM', { mdmDevices: [cloneDeep(appleMdm)] });
      }
    },

    enrollmentProfiles(state = { mdmDevices: [{ id: '' }] }) {
      const mdmDevices = getMdmDevices(state);
      const deviceId = (mdmDevices[0] && mdmDevices[0].id) || '';

      return MdmsApi.getEnrollmentProfiles(deviceId);
    },

    fetchAvailableOSUpdates({ state }, payload) {
      const mdmDevices = getMdmDevices(state);
      return MdmsApi.fetchAvailableOSUpdates(mdmDevices[0].id, payload.deviceId);
    },

    fetchBypassCodes(state = {}, payload) {
      const mdmDevices = getMdmDevices(state);
      const { deviceId } = payload;
      return MdmsApi.fetchBypassCodes(mdmDevices[0].id, deviceId);
    },

    fetchMdmDeviceDetails(state = { mdmDevices: [{ id: '' }] }, payload) {
      const mdmDevices = getMdmDevices(state);
      return MdmsApi.fetchMdmDeviceDetails(mdmDevices[0].id, payload);
    },

    async fetchActivationLockInformation({ commit, state }, payload) {
      const mdmDevices = getMdmDevices(state);
      const { deviceId } = payload;

      if (isEmpty(mdmDevices)) return;

      const { id } = mdmDevices[0];
      const activationLockInfo = await MdmsApi.fetchMdmDeviceDetails(id, deviceId);
      commit('SET_ITEM', { activationLock: cloneDeep(activationLockInfo) });
      commit('SET_PREVIOUS_ITEM', { activationLock: cloneDeep(activationLockInfo) });
    },

    async fetchEnrollmentProfileDownloadUrl(state = { mdmDevices: [{ id: '' }] }) {
      const mdmDevices = getMdmDevices(state);
      const deviceId = (mdmDevices[0] && mdmDevices[0].id) || '';
      const enrollmentProfiles = await MdmsApi.getEnrollmentProfiles(deviceId);
      const enrollmentId = (enrollmentProfiles[0] && enrollmentProfiles[0].id) || '';
      return MdmsApi.fetchEnrollmentProfileDownloadUrl(deviceId, enrollmentId);
    },

    fetchDepDevices(state = {}, limit, skip) {
      const mdmDevices = getMdmDevices(state);
      const params = {
        limit,
        skip,
      };

      return MdmsApi.getDepDevices(mdmDevices[0].id, params);
    },

    async fetchMdmSettings({ commit }) {
      const mdmDevices = await MdmsApi.fetchMdm();

      commit('SET_ITEM', { mdmDevices: cloneDeep(mdmDevices) });
      commit('SET_PREVIOUS_ITEM', { mdmDevices: cloneDeep(mdmDevices) });
    },

    postClearActivationLock(state = {}, payload) {
      const mdmDevices = getMdmDevices(state);
      const { deviceId } = payload;
      if (mdmDevices.length === 0) {
        return null;
      }
      return MdmsApi.postClearActivationLock(mdmDevices[0].id, deviceId);
    },

    postScheduleOSUpdate(state = {}, payload) {
      const mdmDevices = getMdmDevices(state);
      const { deviceId, selectedInstallAction, selectedUpdate } = payload;
      const body = {
        install_action: selectedInstallAction,
        product_key: selectedUpdate,
        max_user_deferrals: 1,
      };
      return MdmsApi.postScheduleOSUpdate(mdmDevices[0].id, deviceId, body);
    },

    refreshDepDevices(state = {}) {
      const mdmDevices = getMdmDevices(state);

      return MdmsApi.postRefreshDepDevices(mdmDevices[0].id);
    },

    refreshActivationLockInformation(state = {}, payload) {
      const mdmDevices = getMdmDevices(state);
      const { deviceId } = payload;
      return MdmsApi.refreshActivationLockInformation(mdmDevices[0].id, deviceId);
    },

    updateDep(state = {}, payload) {
      const mdmDevices = getMdmDevices(state);

      const dep = isEmpty(payload)
        ? {
          defaultIosUserEnrollmentDeviceGroupID:
           mdmDevices[0].defaultIosUserEnrollmentDeviceGroupID,
          defaultSystemGroupID: mdmDevices[0].defaultSystemGroupID,
          dep: mdmDevices[0].dep,
        } : payload;
      return MdmsApi.updateDep(mdmDevices[0].id, dep);
    },
  },

  getters: {
    isApnsPushTopicEmpty(state = {}) {
      const mdmDevices = getMdmDevices(state);

      if (isEmpty(mdmDevices)) return true;

      return mdmDevices[0].apnsPushTopic === '';
    },

    getCreatorAppleId(state = {}) {
      const mdmDevices = getMdmDevices(state);

      return isEmpty(mdmDevices) ? '' : mdmDevices[0].appleCertCreatorAppleID;
    },

    getDmId(state = {}) {
      const mdmDevices = getMdmDevices(state);

      if (isEmpty(mdmDevices)) return '';

      return mdmDevices[0].id;
    },
    getDepTokenState(state = {}) {
      const mdmDevices = getMdmDevices(state);

      if (isEmpty(mdmDevices)) return '';

      return mdmDevices[0].depServerTokenState;
    },
    getDepTokenExpiry(state = {}) {
      const mdmDevices = getMdmDevices(state);

      if (isEmpty(mdmDevices)) return '';

      return mdmDevices[0].depAccessTokenExpiry;
    },
  },
};
